import { Alert, Dropdown, Menu, Modal, Tag } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import React, { useMemo } from "react";
import firebase from "firebase/compat/app";
import { newOpId, stamp } from "../../../modules/helpers/audit";

const ChangeStatusDropdown = ({ record, onConfirm, onStatusChangeInitiated, ...restProps }) => {
  const db = firebase.firestore();

  // Fallback: message.status if exists, otherwise recipients[0].status
  const currentStatus = record.status || (record.recipients && record.recipients[0]?.status) || "prepare";

  // Check if printing fields are filled (for paper sends only)
  const isPaperSend = record.type === "simple" || record.type === "recommande";
  const hasPrintingFields = record.pages > 0 && record.addresses_count > 0;

  const updateRecord = (type) => {
    return db
      .collection("companies")
      .doc(record.companyId)
      .collection("messages")
      .doc(record.key)
      .set(
        {
          status: type,
          status_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
          // Opération reprise par la cascade vers les destinataires et les preuves
          last_change: stamp({ op: newOpId() }),
        },
        { merge: true }
      )
      .then(() => {
        if (onConfirm) onConfirm({ status: type });
      });
  };

  const onMenuItemClick = (menuInfo) => {
    // Notify parent that status change was initiated by this user
    if (onStatusChangeInitiated) {
      onStatusChangeInitiated();
    }

    switch (menuInfo.key) {
      case "sent":
        // Block if paper send and printing fields are not filled
        if (isPaperSend && !hasPrintingFields) {
          Modal.warning({
            title: "Champs obligatoires non renseignés",
            icon: <ExclamationCircleOutlined />,
            width: "650px",
            content: (
              <div>
                <p>Avant de passer au statut "Envoyé", veuillez renseigner :</p>
                <ul>
                  {(!record.pages || record.pages <= 0) && <li>Le nombre de pages</li>}
                  {(!record.addresses_count || record.addresses_count <= 0) && <li>Le nombre d'adresses</li>}
                </ul>
                <p>Ces champs se trouvent dans la section "Impression (Opérateur)".</p>
              </div>
            ),
            okText: "Compris",
          });
          return;
        }
        Modal.confirm({
          title: "Marquer comme envoyé",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: <div>Êtes-vous sûr de vouloir marquer l'envoi comme étant "Envoyé" ?</div>,
          okText: "Oui",
          okType: "primary",
          onOk: () => {
            return updateRecord("sent");
          },
        });
        break;
      case "prepare":
        Modal.confirm({
          title: "Marquer en préparation",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: (
            <div>Êtes-vous sûr de vouloir marquer l'envoi comme étant "En préparation" ?</div>
          ),
          okText: "Oui",
          okType: "primary",
          onOk: () => {
            return updateRecord("prepare");
          },
        });
        break;
      case "error_multiple":
        Modal.confirm({
          title: "Marquer comme erreurs multiples",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: (
            <div>
              <div style={{ marginBottom: "1rem" }}>
                Êtes-vous sûr de vouloir marquer l'envoi comme étant "Erreurs multiples" ?
              </div>
              <Alert type="info" message="Vous pourrez ajuster le statut de chaque destinataire individuellement." />
            </div>
          ),
          okText: "Oui",
          okType: "primary",
          onOk: () => {
            return updateRecord("error_multiple");
          },
        });
        break;
      case "error_address":
        Modal.confirm({
          title: "Marquer comme Erreur d'adressage",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: (
            <div>
              <div style={{ marginBottom: "1rem" }}>
                Êtes-vous sûr de vouloir marquer l'envoi comme étant "Erreur d'adressage" ?
              </div>
              <Alert type="warning" message="Cette action enverra un mail à l'utilisateur!" />
            </div>
          ),
          okText: "Oui",
          okType: "primary",
          onOk: () => {
            return updateRecord("error_address");
          },
        });
        break;
      case "error_file":
        Modal.confirm({
          title: "Marquer comme Erreur fichier",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: (
            <div>
              <div style={{ marginBottom: "1rem" }}>
                Êtes-vous sûr de vouloir marquer l'envoi comme étant "Erreur fichier" ?
              </div>
              <Alert type="warning" message="Cette action enverra un mail à l'utilisateur!" />
            </div>
          ),
          okText: "Oui",
          okType: "primary",
          onOk: () => {
            return updateRecord("error_file");
          },
        });
        break;
      case "error_print":
        Modal.confirm({
          title: "Marquer comme Erreur d'impression",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: (
            <div>
              <div style={{ marginBottom: "1rem" }}>
                Êtes-vous sûr de vouloir marquer l'envoi comme étant "Erreur d'impression" ?
              </div>
              <Alert type="warning" message="Cette action enverra un mail à l'utilisateur!" />
            </div>
          ),
          okText: "Oui",
          okType: "primary",
          onOk: () => {
            return updateRecord("error_print");
          },
        });
        break;
      case "error_print_param":
        Modal.confirm({
          title: "Marquer comme Erreur paramètres d'impression",
          icon: <ExclamationCircleOutlined />,
          width: "650px",
          content: (
            <div>
              <div style={{ marginBottom: "1rem" }}>
                Êtes-vous sûr de vouloir marquer l'envoi comme étant "Erreur paramètres
                d'impression" ?
              </div>
              <Alert type="warning" message="Cette action enverra un mail à l'utilisateur!" />
            </div>
          ),
          okText: "Oui",
          okType: "primary",
          onOk: () => {
            return updateRecord("error_print_param");
          },
        });
        break;
    }
  };
  const menu = (
    <Menu onClick={onMenuItemClick}>
      <Menu.Item key="sent">
        <Tag icon={<CheckCircleOutlined />} color="cyan">
          Envoyé
        </Tag>
      </Menu.Item>
      <Menu.Item key="prepare">
        <Tag icon={<SyncOutlined />} color="processing">
          En préparation
        </Tag>
      </Menu.Item>
      <Menu.Item key="error_multiple">
        <Tag icon={<WarningOutlined />} color="orange">
          Erreurs multiples
        </Tag>
      </Menu.Item>
      <Menu.Item key="error_address">
        <Tag icon={<CloseCircleOutlined />} color="red">
          Erreur d'adressage
        </Tag>
      </Menu.Item>
      <Menu.Item key="error_file">
        <Tag icon={<CloseCircleOutlined />} color="red">
          Erreur fichier
        </Tag>
      </Menu.Item>
      <Menu.Item key="error_print">
        <Tag icon={<CloseCircleOutlined />} color="red">
          Erreur d'impression
        </Tag>
      </Menu.Item>
      <Menu.Item key="error_print_param">
        <Tag icon={<CloseCircleOutlined />} color="red">
          Erreur paramètres d'impression
        </Tag>
      </Menu.Item>
    </Menu>
  );

  const TagMessage = useMemo(() => {
    switch (currentStatus) {
      case "sent":
        return (
          <Tag icon={<CheckCircleOutlined />} color="cyan">
            Envoyé
          </Tag>
        );
      case "error_multiple":
        return (
          <Tag icon={<WarningOutlined />} color="orange">
            Erreurs multiples
          </Tag>
        );
      case "error_address":
        return (
          <Tag icon={<CloseCircleOutlined />} color="red">
            Erreur d'adressage
          </Tag>
        );
      case "error_file":
        return (
          <Tag icon={<CloseCircleOutlined />} color="red">
            Erreur fichier
          </Tag>
        );
      case "error_print":
        return (
          <Tag icon={<CloseCircleOutlined />} color="red">
            Erreur d'impression
          </Tag>
        );
      case "error_print_param":
        return (
          <Tag icon={<CloseCircleOutlined />} color="red">
            Erreur paramètres d'impression
          </Tag>
        );
      case "cancelled":
        return (
          <Tag icon={<CloseCircleOutlined />} color="red">
            Annulé
          </Tag>
        );
      case "prepare":
      default:
        return (
          <Tag icon={<SyncOutlined />} color="processing">
            En préparation
          </Tag>
        );
    }
  }, [currentStatus]);

  // Un envoi annulé n'offre plus aucun changement de statut (décision 17/08/2026)
  if (currentStatus === "cancelled") {
    return TagMessage;
  }

  return (
    <Dropdown.Button
      overlay={menu}
      icon={<DownOutlined />}
      trigger="click"
      className="table-dropdown-status"
      {...restProps}
    >
      {TagMessage}
    </Dropdown.Button>
  );
};

export default ChangeStatusDropdown;
